import moment from "moment";
import {
  MOTOR_KEYS,
  isDonePart,
  joinNames,
  realParts,
  shortLabel,
  sortParts,
} from "./policyParts";

/**
 * "What did this edit change, and for which part?" — the basis of the
 * Re-submit parts panel on the last step of an edit.
 *
 * Fields belong to a part like this:
 *   OD only ........ IDV, discount, Nil Dip (zero depreciation), add-ons
 *   TP only ........ TPPD restriction
 *   PA ............. PA cover on/off, amount, company, nominee details
 *   shared ......... customer, address, vehicle identity, engine / chassis,
 *                    RTO, purchase / registration dates, financier
 * A single-company `motor` part is OD + TP, so it takes both sets. Brisk
 * builds its certificate from the customer and vehicle, so a Brisk PA takes the
 * shared fields too. Nominee details also matter to a National motor part,
 * whose portal asks for a nominee on every quote.
 */

const MAX_FIELDS = 6;

const truthy = (v) => v === true || String(v || "").trim().toLowerCase() === "true";
const blank = (v) => v === undefined || v === null || v === "" || (typeof v === "number" && Number.isNaN(v));
const trimmed = (v) => (blank(v) ? "" : String(v).trim());
const round2 = (n) => Number(Number(n).toFixed(2));

const dayOf = (v) => {
  if (blank(v)) return "";
  const raw = v && v.$date ? v.$date : v;
  const m = moment(raw);
  return m.isValid() ? m.format("YYYY-MM-DD") : String(raw);
};

const joinText = (...vals) => vals.map(trimmed).filter(Boolean).join(" ");
const joinComma = (...vals) => vals.map(trimmed).filter(Boolean).join(", ");

const paCompanyOf = (s) => {
  if (!truthy(s.paCover)) return "";
  if (s.settings && s.settings.PACompany) return s.settings.PACompany;
  if (String(s.paCoverCompany || "").toLowerCase() === "brisk") return "Brisk";
  return s.paCoverInsurerCompany || s.company || "";
};

/** The insurer a part is (or would be) bought from. */
const insurerOf = (s, key) => {
  const st = s.settings || {};
  if (key === "tp") return st.TPCompany || s.tpCompany || s.company || "";
  return st.ODCompany || s.odCompany || s.company || "";
};

const vehicleName = (s) => joinText(s.vehicleMake, s.vehicleModel);

// owner: "od" | "tp" | "pa" | "nominee" | "shared". Order matters: the part's
// own fields come first, so the cap keeps the most relevant ones.
const FIELD_DEFS = [
  { key: "idv", label: "IDV", owner: "od", type: "money" },
  { key: "discount", label: "Discount", owner: "od", type: "percent" },
  { key: "zeroDepreciation", label: "Nil Dip (zero depreciation)", owner: "od", type: "bool" },
  { key: "zeroDepreciationPercentage", label: "Nil Dip amount", owner: "od", type: "money" },
  { key: "addOns", label: "Add-ons", owner: "od", type: "list" },
  { key: "tppdRestrict", label: "TPPD restriction", owner: "tp", type: "bool" },

  { key: "paCover", label: "PA cover", owner: "pa", type: "onoff" },
  { key: "paCoverAmount", label: "PA cover amount", owner: "pa", type: "money" },
  // owner "nominee": the PA term is read by a National TP / motor part (its portal's "No of Years"), like the nominee
  // is; a PA part still lists it, since "pa" parts take the nominee group too.
  { key: "paCoverYears", label: "PA cover years", owner: "nominee", type: "text" },
  { key: "paCompany", label: "PA company", owner: "pa", type: "text", read: paCompanyOf },

  { key: "nomineeName", label: "Nominee name", owner: "nominee", type: "text" },
  { key: "nomineeRelation", label: "Nominee relation", owner: "nominee", type: "text" },
  { key: "nomineeGender", label: "Nominee gender", owner: "nominee", type: "text" },
  { key: "nomineeDob", label: "Nominee date of birth", owner: "nominee", type: "date" },
  { key: "nomineeAge", label: "Nominee age", owner: "nominee", type: "text" },
  { key: "appointeeName", label: "Appointee name", owner: "nominee", type: "text" },
  { key: "nomineeType", label: "Nominee type", owner: "nominee", type: "text" },
  { key: "nomineeShare", label: "Nominee share", owner: "nominee", type: "text" },

  { key: "customerName", label: "Customer name", owner: "shared", type: "text", read: (s) => joinText(s.fullName ?? s.firstName, s.surname ?? s.lastName) },
  { key: "dateOfBirth", label: "Date of birth", owner: "shared", type: "date" },
  { key: "gender", label: "Gender", owner: "shared", type: "text" },
  { key: "proposerTitle", label: "Title", owner: "shared", type: "text" },
  { key: "fatherName", label: "Father's name", owner: "shared", type: "text" },
  { key: "mobileNumber", label: "Mobile", owner: "shared", type: "text" },
  { key: "email", label: "Email", owner: "shared", type: "text" },
  {
    key: "address",
    label: "Address",
    owner: "shared",
    type: "text",
    read: (s) =>
      joinComma(s.flatDoorNo, s.floorNo, s.buildingName, s.blockName, s.roadStreetLane, s.areaAndLocality, s.state, s.pincode),
  },
  { key: "makeModel", label: "Make & model", owner: "shared", type: "text", show: vehicleName },
  { key: "vehicleVariant", label: "Variant", owner: "shared", type: "text" },
  { key: "engineNumber", label: "Engine number", owner: "shared", type: "text" },
  { key: "chassisNumber", label: "Chassis number", owner: "shared", type: "text" },
  { key: "rtoCityLocation", label: "RTO", owner: "shared", type: "text" },
  {
    key: "manufactured",
    label: "Manufactured",
    owner: "shared",
    type: "text",
    read: (s) => joinComma(s.manufacturingMonth, s.manufacturingYear),
  },
  { key: "purchaseDate", label: "Purchase date", owner: "shared", type: "date" },
  { key: "registrationDate", label: "Registration date", owner: "shared", type: "date" },
  { key: "hasFinancier", label: "Financier", owner: "shared", type: "onoff" },
  { key: "financierName", label: "Financier name", owner: "shared", type: "text" },
  { key: "financierType", label: "Financier type", owner: "shared", type: "text" },
  { key: "financierAddress", label: "Financier address", owner: "shared", type: "text" },
];

const readField = (def, src) => (def.read ? def.read(src) : src[def.key]);

const same = (type, a, b) => {
  if (type === "bool" || type === "onoff") return truthy(a) === truthy(b);
  if (type === "money") {
    return round2(blank(a) ? 0 : a) === round2(blank(b) ? 0 : b);
  }
  if (type === "percent") {
    if (blank(a) || blank(b)) return blank(a) && blank(b);
    return Number(a) === Number(b);
  }
  if (type === "date") return dayOf(a) === dayOf(b);
  if (type === "list") {
    const flat = (v) => (Array.isArray(v) ? v.map(trimmed).filter(Boolean).sort().join("|") : trimmed(v));
    return flat(a).toLowerCase() === flat(b).toLowerCase();
  }
  return trimmed(a).toLowerCase() === trimmed(b).toLowerCase();
};

const rupees = (v) => `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const show = (type, v) => {
  if (type === "bool") return truthy(v) ? "Yes" : "No";
  if (type === "onoff") return truthy(v) ? "On" : "Off";
  if (type === "date") return blank(v) ? "—" : moment(v && v.$date ? v.$date : v).format("DD/MM/YYYY");
  if (type === "money") return blank(v) || Number(v) === 0 ? "—" : rupees(v);
  if (type === "percent") return blank(v) ? "—" : `${Number(v)}%`;
  if (type === "list") {
    const text = Array.isArray(v) ? v.map(trimmed).filter(Boolean).join(", ") : trimmed(v);
    return text || "—";
  }
  const text = trimmed(v);
  if (!text) return "—";
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
};

/** Which field groups a part carries. */
const ownersFor = (part, nationalNominee) => {
  const owners = [];
  if (part.key === "motor") owners.push("od", "tp");
  else if (part.key === "od") owners.push("od");
  else if (part.key === "tp") owners.push("tp");
  else if (part.key === "pa") owners.push("pa", "nominee");

  if (MOTOR_KEYS.includes(part.key)) {
    owners.push("shared");
    if (nationalNominee(part)) owners.push("nominee");
  } else if (part.kind === "brisk") {
    owners.push("shared");
  }
  return owners;
};

const LEG_KEYS = { motor: ["motor", "od", "tp"], od: ["od"], tp: ["tp"], pa: ["pa"] };

const legTotal = (split, key) => {
  const legs = split && Array.isArray(split.legs) ? split.legs : null;
  if (!legs) return null;
  const hit = legs.filter((l) => (LEG_KEYS[key] || []).includes(l.key));
  return hit.length ? round2(hit.reduce((sum, l) => sum + (Number(l.total) || 0), 0)) : null;
};

/**
 * @param {object} args
 * @param {object} args.savedPolicy   the policy as saved (GET /onlinePolicy/:id)
 * @param {object} args.newValues     the form's values (may carry vehicleMake / vehicleModel names)
 * @param {Array}  args.parts         the job's parts
 * @param {object} [args.savedSplit]  premium split the policy was sold with (splitFromPolicy)
 * @param {object} [args.newSplit]    premium split of the edited data (splitFromCalculation)
 * @returns {Object<string, {fields: Array<{label, from, to}>, overflow: number,
 *   total: number, premium: ({from, to}|null), changed: boolean}>} keyed by part key
 */
export const changedByPart = ({ savedPolicy, newValues, parts, savedSplit, newSplit } = {}) => {
  const saved = savedPolicy || {};
  const next = newValues || {};
  const out = {};

  const isNationalPart = (part) => String(part.company || "").trim().toLowerCase() === "national";

  for (const part of realParts(parts)) {
    if (part.status === "cancelled") continue;
    const owners = ownersFor(part, isNationalPart);
    const fields = [];

    // A different insurer is the most consequential change there is.
    if (part.key !== "pa") {
      const from = insurerOf(saved, part.key);
      const to = insurerOf(next, part.key);
      if (!same("text", from, to) && (trimmed(from) || trimmed(to))) {
        fields.push({ label: "Insurer", from: show("text", from), to: show("text", to) });
      }
    }

    for (const owner of ["od", "tp", "pa", "nominee", "shared"]) {
      if (!owners.includes(owner)) continue;
      for (const def of FIELD_DEFS) {
        if (def.owner !== owner) continue;
        const a = readField(def, saved);
        const b = readField(def, next);
        // A cleared box and a missing value are the same thing.
        if (blank(a) && blank(b)) continue;
        // The PA details are moot while PA is off on both sides.
        if ((def.owner === "pa" || def.owner === "nominee") && def.key !== "paCover" && part.key === "pa" && !truthy(saved.paCover) && !truthy(next.paCover)) continue;
        if (same(def.type, a, b)) continue;
        const fromText = def.show && !blank(saved.makeModel) ? def.show(saved) : null;
        const toText = def.show && !blank(next.makeModel) ? def.show(next) : null;
        fields.push({
          label: def.label,
          from: fromText || show(def.type, a),
          to: toText || show(def.type, b),
        });
      }
    }

    const fromTotal = legTotal(savedSplit, part.key);
    const toTotal = legTotal(newSplit, part.key);
    const premium =
      fromTotal !== null && toTotal !== null && fromTotal !== toTotal
        ? { from: fromTotal, to: toTotal }
        : null;

    out[part.key] = {
      fields: fields.slice(0, MAX_FIELDS),
      overflow: Math.max(0, fields.length - MAX_FIELDS),
      total: fields.length,
      premium,
      changed: fields.length > 0 || !!premium,
    };
  }
  return out;
};

/**
 * One row per part for the Re-submit panel, and the keys that will be re-queued.
 *
 *  - bought ......... locked, unticked (an unpaid payment-link policy may
 *                     re-submit its motor parts; a bought Brisk PA never)
 *  - running ........ locked, unticked
 *  - not started .... ticked and locked: it runs with the new data anyway
 *  - failed / stopped ticked by default when its details or premium changed
 *  - Brisk PA ....... follows the part it depends on when that is ticked
 *  - PA bought with another part just mirrors it
 *
 * @param {object} args.overrides  { [partKey]: boolean } the operator's own ticks
 */
export const resubmitPlan = ({ parts, changes = {}, overrides = {}, paymentLink = false, buyAgain = false } = {}) => {
  const list = sortParts(realParts(parts).filter((p) => p.status !== "cancelled"));
  const state = {};

  const decide = (p) => {
    if (p.status === "processing") {
      return { locked: true, checked: false, state: "running", reason: "Running now — cannot be re-submitted" };
    }
    const changed = !!(changes[p.key] && changes[p.key].changed);
    const own = Object.prototype.hasOwnProperty.call(overrides, p.key);
    if (isDonePart(p)) {
      const motor = MOTOR_KEYS.includes(p.key) && p.kind !== "brisk";
      const unlock = paymentLink && motor;
      if (!unlock && buyAgain && motor) {
        // A wallet policy: buying a bought part AGAIN is a new purchase, so it is never ticked by itself.
        return {
          locked: false,
          checked: own ? !!overrides[p.key] : false,
          state: "bought-again",
          reason: "Already bought. Tick to buy it again: the full new amount is charged once more and the old payment stays",
        };
      }
      if (!unlock) {
        return { locked: true, checked: false, state: "bought", reason: "Already bought — not re-submitted" };
      }
      return {
        locked: false,
        checked: own ? !!overrides[p.key] : changed,
        state: "bought-link",
        reason: "Payment link not paid yet — can be re-submitted",
      };
    }
    if (p.status === "pending") {
      return { locked: true, checked: true, state: "pending", reason: "Not started yet — will run with the new data" };
    }
    return {
      locked: false,
      checked: own ? !!overrides[p.key] : changed,
      state: "failed",
      reason: changed ? "Details changed — tick to re-submit" : "No change — tick to re-submit anyway",
    };
  };

  for (const p of list) {
    if (p.kind !== "brisk" && p.kind !== "withPart") state[p.key] = decide(p);
  }
  for (const p of list) {
    if (p.kind !== "brisk") continue;
    const dep = list.find((x) => x.key === p.dependsOn);
    const depOn = dep && state[dep.key] && state[dep.key].checked;
    state[p.key] =
      depOn && !isDonePart(p)
        ? { locked: true, checked: true, state: "follows", follows: dep.key, reason: `Follows ${shortLabel(dep.key)}` }
        : decide(p);
  }
  for (const p of list) {
    if (p.kind !== "withPart") continue;
    const owner = state[p.mirrors];
    state[p.key] = {
      locked: true,
      checked: !!(owner && owner.checked),
      state: "with",
      with: p.mirrors,
      reason: `Bought with ${shortLabel(p.mirrors)}`,
    };
  }

  const rows = list.map((p) => ({ part: p, ...state[p.key] }));
  const picked = rows.filter((r) => r.checked && r.part.kind !== "withPart").map((r) => r.part);
  return { rows, keys: picked.map((p) => p.key), parts: picked };
};

/** Button / summary wording for the parts about to be re-submitted. */
export const resubmitLabel = (keys, parts) => {
  const picked = realParts(parts).filter((p) => (keys || []).includes(p.key));
  if (!picked.length) return { summary: "Nothing will be re-submitted — only the policy details are updated.", button: "Update only — nothing re-submitted" };
  const names = joinNames(picked, { conjunction: "" });
  return picked.length === 1
    ? { summary: `${names} will be re-submitted.`, button: `Update & re-submit ${names}` }
    : {
        summary: `${picked.length} parts will be re-submitted: ${names}.`,
        button: `Update & re-submit ${picked.length} parts (${names})`,
      };
};

/**
 * Wallet legs (od / tp / motor / pa) that belong to parts already bought.
 * The backend freezes them on an edit — no refund, no fresh debit — so the
 * balance check leaves them out too.
 */
export const frozenLegKeys = (parts, { paymentLink = false, boughtAgain = [] } = {}) => {
  const out = new Set();
  for (const p of realParts(parts)) {
    if (!isDonePart(p)) continue;
    if (paymentLink && MOTOR_KEYS.includes(p.key) && p.kind !== "brisk") continue;
    // A bought part the editor re-buys is charged afresh, so its legs are not left out of the new charge.
    if (boughtAgain.includes(p.key)) continue;
    if (p.key === "motor") ["motor", "od", "tp"].forEach((k) => out.add(k));
    else out.add(p.key);
  }
  return [...out];
};

/**
 * The bought motor parts (never a Brisk certificate) that are ticked to be bought AGAIN on a wallet policy edit.
 * Each is a new purchase: the server charges the full new amount once more and keeps the old payment. Empty for a
 * payment-link policy (nothing was paid, so re-submitting is free) and when nothing bought is ticked.
 */
export const boughtAgainKeys = (parts, resubmitKeys, { paymentLink = false } = {}) => {
  if (paymentLink || !Array.isArray(resubmitKeys)) return [];
  return realParts(parts)
    .filter((p) => isDonePart(p) && MOTOR_KEYS.includes(p.key) && p.kind !== "brisk" && resubmitKeys.includes(p.key))
    .map((p) => p.key);
};
